/**
 * The notification levels of a module's contexts as master computes them
 * (I-3826): `GET /v1/notifications/contexts?moduleKey=&contextType=`. Master owns
 * the rule — a context's own level (`shell` → `notifications.levels`), else
 * the default level of its module and context type
 * (`notifications.default_levels`), else „all“, made at least „mentions“ by
 * a mute that holds — so an app shows the answer and derives nothing.
 *
 * One store per module and context type and tab: every component of an app
 * reads the same answer. It is read again after a write of the levels or
 * mutes (`refresh`), when the shell's settings change, and when the first
 * mute it lists runs out.
 */
import { computed, effectScope, shallowRef, watch, type ComputedRef, type Ref } from "vue";
import { fetchWithAuth, getApiOrigin } from "../auth/fetch";
import { useAccountConfig } from "./accountConfig";
import {
  NOTIFICATION_LEVELS,
  NOTIFICATION_SETTINGS_KEY,
  type NotificationLevel,
} from "./notificationMutes";
import type { JsonObject } from "./mergePatch";

/** One context with a level of its own or a mute that holds now. */
export interface NotificationContextLevel {
  contextId: string;
  /** The context's own level; null: the default level applies. */
  level: NotificationLevel | null;
  /** A mute holds now; `mutedUntil` is its end, null without end. */
  muted: boolean;
  mutedUntil: string | null;
  /** The level that applies now. */
  effectiveLevel: NotificationLevel;
}

/** Master's answer for one module and context type. */
export interface NotificationContextLevels {
  module: string;
  contextType: string;
  /** The level of every context not listed. */
  defaultLevel: NotificationLevel;
  contexts: NotificationContextLevel[];
}

function isLevel(value: unknown): value is NotificationLevel {
  return (NOTIFICATION_LEVELS as readonly unknown[]).includes(value);
}

/** Master's answer, checked; null when it is none. */
export function parseNotificationContextLevels(
  body: unknown,
): NotificationContextLevels | null {
  const raw = body as Partial<NotificationContextLevels> | null;
  if (!raw || typeof raw !== "object" || !isLevel(raw.defaultLevel)) return null;
  const contexts: NotificationContextLevel[] = [];
  for (const item of Array.isArray(raw.contexts) ? raw.contexts : []) {
    const c = item as Partial<NotificationContextLevel> | null;
    if (!c || typeof c.contextId !== "string" || !isLevel(c.effectiveLevel)) {
      continue;
    }
    contexts.push({
      contextId: c.contextId,
      level: isLevel(c.level) ? c.level : null,
      muted: c.muted === true,
      mutedUntil: typeof c.mutedUntil === "string" ? c.mutedUntil : null,
      effectiveLevel: c.effectiveLevel,
    });
  }
  return {
    module: String(raw.module ?? ""),
    contextType: String(raw.contextType ?? ""),
    defaultLevel: raw.defaultLevel,
    contexts,
  };
}

/** Reads master's answer; null when it failed. */
export async function fetchNotificationContextLevels(
  module: string,
  contextType: string,
): Promise<NotificationContextLevels | null> {
  try {
    // `moduleKey`, not `module`: the gateway routes by that parameter.
    const query = new URLSearchParams({ moduleKey: module, contextType });
    const response = await fetchWithAuth(
      `${getApiOrigin()}/v1/notifications/contexts?${query}`,
      { cache: "no-store" },
    );
    if (!response.ok) return null;
    return parseNotificationContextLevels(await response.json());
  } catch {
    return null;
  }
}

export interface NotificationContextLevelsStore {
  /** Master's last answer; null until it answered. */
  state: Ref<NotificationContextLevels | null>;
  /** The state of one context: its entry, or the default for one not listed. */
  context(contextId: () => string | null): ComputedRef<NotificationContextLevel | null>;
  /** Reads master's answer again. */
  refresh(): Promise<void>;
}

const stores = new Map<string, NotificationContextLevelsStore>();

/** The longest a timer waits for a mute to run out (a browser's limit). */
const MAX_TIMER_MS = 2 ** 31 - 1;

function createStore(
  module: string,
  contextType: string,
): NotificationContextLevelsStore {
  const state = shallowRef<NotificationContextLevels | null>(null);
  let timer: ReturnType<typeof setTimeout> | undefined;
  let generation = 0;

  /** Reads again when the first listed mute runs out: its level comes back. */
  function scheduleExpiry(answer: NotificationContextLevels) {
    if (timer) clearTimeout(timer);
    timer = undefined;
    const ends = answer.contexts
      .map((c) => (c.mutedUntil ? Date.parse(c.mutedUntil) : NaN))
      .filter((end) => Number.isFinite(end));
    if (!ends.length) return;
    const wait = Math.min(Math.max(Math.min(...ends) - Date.now(), 0) + 1000, MAX_TIMER_MS);
    timer = setTimeout(() => void refresh(), wait);
  }

  async function refresh(): Promise<void> {
    const mine = ++generation;
    const answer = await fetchNotificationContextLevels(module, contextType);
    if (mine !== generation || !answer) return;
    state.value = answer;
    scheduleExpiry(answer);
  }

  function context(
    contextId: () => string | null,
  ): ComputedRef<NotificationContextLevel | null> {
    return computed(() => {
      const id = contextId();
      const answer = state.value;
      if (!id || !answer) return null;
      return (
        answer.contexts.find((c) => c.contextId === id) ?? {
          contextId: id,
          level: null,
          muted: false,
          mutedUntil: null,
          effectiveLevel: answer.defaultLevel,
        }
      );
    });
  }

  // A change of the shell's settings (another tab, the settings list, a
  // design) may change the levels: master answers again.
  // The store lives for the tab, not for the component that first asked:
  // its watcher runs in a scope of its own.
  effectScope(true).run(() => {
    const shell = useAccountConfig<JsonObject>(NOTIFICATION_SETTINGS_KEY);
    watch(
      () => shell.effective.value,
      () => void refresh(),
      { immediate: true },
    );
  });

  return { state, context, refresh };
}

/** The levels of `module`'s contexts of `contextType`, one store per tab. */
export function useNotificationContextLevels(
  module: string,
  contextType: string,
): NotificationContextLevelsStore {
  const key = `${module}\u0000${contextType}`;
  let store = stores.get(key);
  if (!store) {
    store = createStore(module, contextType);
    stores.set(key, store);
  }
  return store;
}

/** Forgets every store (tests, a sign-out). */
export function resetNotificationContextLevels(): void {
  stores.clear();
}
